import type { TileData, TilePosition } from '../../model/gamestate/GameState';
import type { EntityManager } from './EntityManager';
import type { MapLayerRenderer } from './MapLayerRenderer';
import type {
    ActorHoverChangedDetail,
    ActorTargetDetail,
    HoveredActorInfo,
    PixelPosition,
    TileSelectedDetail,
} from './types';
import type { PointerEventName } from './constants';
import type { Viewport } from './viewport';

export class InteractionManager {
    private readonly pointerHandlers = new Map<PointerEventName, (event: PointerEvent) => void>();
    private contextMenuHandler: ((event: MouseEvent) => void) | null = null;

    private hoveredTile: TilePosition | null = null;
    private hoveredTileInFog = false;
    private hoveredActor: HoveredActorInfo | null = null;
    private hoverPointer: PixelPosition | null = null;
    private skillTargetingActive = false;

    private readonly canvas: HTMLCanvasElement;
    private readonly viewport: Viewport;
    private readonly mapLayer: MapLayerRenderer;
    private readonly entityManager: EntityManager;
    private readonly eventTarget: EventTarget;
    private readonly tileSize: number;

    private isPortalTransitioningFn: () => boolean;
    private getLastCharacterPositionFn: () => TilePosition | null;
    private showEntityContextMenuFn: (pointer: PixelPosition, actor: HoveredActorInfo) => void;
    private hideEntityContextMenuFn: () => void;
    private requestRenderFn: () => void;
    private getActiveRadiusFn: () => number;
    private allowGhostTileSelectionFn: () => boolean;

    constructor(
        canvas: HTMLCanvasElement,
        viewport: Viewport,
        mapLayer: MapLayerRenderer,
        entityManager: EntityManager,
        eventTarget: EventTarget,
        tileSize: number,
        callbacks: {
            isPortalTransitioning: () => boolean;
            getLastCharacterPosition: () => TilePosition | null;
            showEntityContextMenu: (pointer: PixelPosition, actor: HoveredActorInfo) => void;
            hideEntityContextMenu: () => void;
            requestRender: () => void;
            getActiveRadius: () => number;
            allowGhostTileSelection: () => boolean;
        }
    ) {
        this.canvas = canvas;
        this.viewport = viewport;
        this.mapLayer = mapLayer;
        this.entityManager = entityManager;
        this.eventTarget = eventTarget;
        this.tileSize = tileSize;

        this.isPortalTransitioningFn = callbacks.isPortalTransitioning;
        this.getLastCharacterPositionFn = callbacks.getLastCharacterPosition;
        this.showEntityContextMenuFn = callbacks.showEntityContextMenu;
        this.hideEntityContextMenuFn = callbacks.hideEntityContextMenu;
        this.requestRenderFn = callbacks.requestRender;
        this.getActiveRadiusFn = callbacks.getActiveRadius;
        this.allowGhostTileSelectionFn = callbacks.allowGhostTileSelection;
    }

    public setSkillTargetingActive(active: boolean): void {
        this.skillTargetingActive = active;
    }

    public getHoveredTile(): { tile: TilePosition | null; inFog: boolean } {
        return { tile: this.hoveredTile, inFog: this.hoveredTileInFog };
    }

    public getHoveredActor(): HoveredActorInfo | null {
        return this.hoveredActor;
    }

    public getHoverPointer(): PixelPosition | null {
        return this.hoverPointer;
    }

    public attachPointerEvents(): void {
        const target = this.canvas;

        const moveHandler = (event: PointerEvent): void => {
            const pointer = this.getPointerCoordinates(event);
            this.hoverPointer = pointer ? { ...pointer } : null;
            const actor = pointer ? this.getActorAtScreen(pointer) : null;
            this.setHoveredActor(actor);

            const tile = this.getTileFromPointerEvent(event);
            if (!tile) {
                this.clearHover();
                if (actor) {
                    this.canvas.style.cursor = 'pointer';
                    this.requestRenderFn();
                } else {
                    this.canvas.style.cursor = '';
                }
                return;
            }
            const lastCharPos = this.getLastCharacterPositionFn();
            const isFogged = Boolean(lastCharPos && !this.isWithinActiveRadius(tile, lastCharPos));
            this.updateHover(tile, isFogged);
            if (actor) {
                this.canvas.style.cursor = 'pointer';
                this.requestRenderFn();
            }

            if ((event.buttons & 0b11) !== 0) {
                this.handleTileClick(tile, this.resolveTileData(tile), event);
            }
        };

        const leaveHandler = (): void => {
            this.hoverPointer = null;
            this.setHoveredActor(null);
            this.clearHover();
        };

        const clickHandler = (event: PointerEvent): void => {
            this.hideEntityContextMenuFn();
            // Allow standard click (button 0)
            if (event.button !== 0) {
                return;
            }
            const pointer = this.getPointerCoordinates(event);
            const actor = pointer ? this.getActorAtScreen(pointer) : null;
            const actorDetail = actor ? this.toActorTargetDetail(actor) : null;
            if (actorDetail) {
                const actorSelectedEvent = new CustomEvent<ActorTargetDetail>('actorSelected', {
                    detail: actorDetail,
                    cancelable: true,
                });
                this.eventTarget.dispatchEvent(actorSelectedEvent);
                if (actorSelectedEvent.defaultPrevented) {
                    return;
                }
            }
            const tile = this.getTileFromPointerEvent(event);
            if (tile) {
                this.handleTileClick(tile, this.resolveTileData(tile), event);
            }
        };

        this.pointerHandlers.set('pointermove', moveHandler);
        this.pointerHandlers.set('pointerleave', leaveHandler);
        this.pointerHandlers.set('click', clickHandler);

        target.addEventListener('pointermove', moveHandler);
        target.addEventListener('pointerleave', leaveHandler);
        target.addEventListener('click', clickHandler);

        this.contextMenuHandler = (event: MouseEvent): void => {
            if (this.skillTargetingActive) {
                event.preventDefault();
                this.hideEntityContextMenuFn();
                this.eventTarget.dispatchEvent(new Event('skillTargetingCancelled'));
                return;
            }
            const pointer = this.getPointerCoordinates(event);
            const actor = pointer ? this.getContextTargetAtScreen(pointer) : null;
            if (!pointer) {
                this.hideEntityContextMenuFn();
                return;
            }

            if (actor) {
                event.preventDefault();
                event.stopImmediatePropagation();
                this.showEntityContextMenuFn(pointer, actor);
                return;
            }

            // No actor, check for tile
            const tile = this.getTileFromPointerEvent(event as PointerEvent);
            if (tile) {
                event.preventDefault();
                this.hideEntityContextMenuFn();
                this.handleTileClick(tile, this.resolveTileData(tile), event);
            }
        };
        target.addEventListener('contextmenu', this.contextMenuHandler);
    }

    public detachPointerEvents(): void {
        const target = this.canvas;
        for (const [eventName, handler] of this.pointerHandlers) {
            target.removeEventListener(eventName, handler);
        }
        this.pointerHandlers.clear();
        if (this.contextMenuHandler) {
            target.removeEventListener('contextmenu', this.contextMenuHandler);
            this.contextMenuHandler = null;
        }
    }

    public checkHoveredActorExists(): void {
        if (this.hoveredActor && !this.entityManager.getActorVisuals().has(this.hoveredActor.id)) {
            this.setHoveredActor(null);
            this.hideEntityContextMenuFn();
        }
    }

    private handleTileClick(tilePosition: TilePosition, tile: TileData | null, pointerEvent?: PointerEvent | MouseEvent): void {
        const pointer = pointerEvent ? this.getPointerCoordinates(pointerEvent) : null;
        const worldPoint = pointer ? this.viewport.screenToWorld(pointer.x, pointer.y) : null;
        const event = new CustomEvent<TileSelectedDetail>('tileSelected', {
            detail: {
                tile,
                tilePosition: { x: tilePosition.x, y: tilePosition.y },
                mapPosition: worldPoint ? { x: worldPoint.x / this.tileSize, y: worldPoint.y / this.tileSize } : undefined,
                screenPosition: pointer ? { x: pointer.x, y: pointer.y } : undefined,
                shiftKey: pointerEvent?.shiftKey,
                button: pointerEvent?.button,
                buttons: 'buttons' in (pointerEvent ?? {}) ? pointerEvent.buttons : undefined,
            }
        });
        this.eventTarget.dispatchEvent(event);
    }

    private getTileFromPointerEvent(event: PointerEvent): TilePosition | null {
        const coords = this.getPointerCoordinates(event);
        if (!coords || this.isPortalTransitioningFn()) {
            return null;
        }

        const map = this.mapLayer.getMap();
        if (!map) return null;
        const grid = this.mapLayer.getGrid();
        const tilePos = this.viewport.screenToTile(
            coords.x,
            coords.y,
            grid,
            map.width,
            map.height,
            this.allowGhostTileSelectionFn() ? 1 : 0,
        );

        if (!tilePos) {
            return null;
        }
        return tilePos;
    }

    private resolveTileData(tilePos: TilePosition): TileData | null {
        const map = this.mapLayer.getMap();
        const tile = map.tiles[tilePos.y]?.[tilePos.x] ?? null;
        if (tile) {
            return tile;
        }
        if (!this.allowGhostTileSelectionFn()) {
            return null;
        }
        return {
            x: tilePos.x,
            y: tilePos.y,
            terrain: 'floor',
            walkable: false,
            pathValue: 0,
        };
    }

    private getPointerCoordinates(event: PointerEvent | MouseEvent): { x: number; y: number } | null {
        const rect = this.canvas.getBoundingClientRect();
        if (!rect.width || !rect.height) {
            return null;
        }
        return {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top
        };
    }

    private updateHover(tile: TilePosition, isFogged: boolean): void {
        if (this.hoveredTile && this.hoveredTile.x === tile.x && this.hoveredTile.y === tile.y) {
            this.hoveredTileInFog = isFogged;
            return;
        }
        this.hoveredTile = { ...tile };
        this.hoveredTileInFog = isFogged;
        this.eventTarget.dispatchEvent(new CustomEvent('tileHoverChanged', {
            detail: {
                tile: { ...tile },
                inFog: isFogged,
            }
        }));
        this.canvas.style.cursor = 'pointer';
        this.requestRenderFn();
    }

    private clearHover(): void {
        if (!this.hoveredTile) {
            if (!this.hoveredActor) {
                this.canvas.style.cursor = '';
            }
            return;
        }
        this.hoveredTile = null;
        this.hoveredTileInFog = false;
        this.eventTarget.dispatchEvent(new CustomEvent('tileHoverChanged', {
            detail: {
                tile: null,
                inFog: false,
            }
        }));
        this.canvas.style.cursor = this.hoveredActor ? 'pointer' : '';
        this.requestRenderFn();
    }

    public setHoveredActor(actor: HoveredActorInfo | null): void {
        const previousId = this.hoveredActor?.id ?? null;
        const nextId = actor?.id ?? null;
        if (previousId === nextId && previousId !== null) {
            this.hoveredActor = actor;
            return;
        }
        this.hoveredActor = actor;
        this.eventTarget.dispatchEvent(new CustomEvent<ActorHoverChangedDetail>('actorHoverChanged', {
            detail: {
                actor: actor ? this.toActorTargetDetail(actor) : null,
            }
        }));
        this.requestRenderFn();
    }

    private toActorTargetDetail(actor: HoveredActorInfo): ActorTargetDetail | null {
        return {
            actorId: actor.id,
            kind: actor.kind,
            displayName: actor.displayName?.trim() || actor.id,
            position: { x: actor.position.x, y: actor.position.y },
        };
    }

    private getActorAtScreen(pointer: PixelPosition): HoveredActorInfo | null {
        let best: HoveredActorInfo | null = null;
        let bestDistanceSq = Number.POSITIVE_INFINITY;
        const hitRadius = this.tileSize * 0.5;
        const hitRadiusSq = hitRadius * hitRadius;

        const actorVisuals = this.entityManager.getActorVisuals();
        const lastCharPos = this.getLastCharacterPositionFn();

        for (const state of actorVisuals.values()) {
            const alphaMultiplier = lastCharPos && !this.isWithinActiveRadius(state.current, lastCharPos) ? 0.35 : 1;
            const alpha = state.opacity * alphaMultiplier;

            if (alpha <= 0) continue;
            const center = this.viewport.worldCenterToScreen(state.currentCenter);
            const dx = pointer.x - center.x;
            const dy = pointer.y - center.y;
            const distanceSq = dx * dx + dy * dy;
            if (distanceSq > hitRadiusSq || distanceSq >= bestDistanceSq) {
                continue;
            }

            const actor = state.data;
            best = {
                id: actor.id,
                playerId: actor.playerId,
                kind: actor.kind,
                entityType: actor.entityType,
                center,
                position: { x: actor.position.x, y: actor.position.y },
                displayName: actor.displayName,
                tradeTargetName: actor.kind === 'remotePlayer'
                    ? (actor.displayName?.trim() || actor.id)
                    : undefined,
                hp: actor.hp,
                maxHp: actor.maxHp,
                stamina: actor.stamina,
                maxStamina: actor.maxStamina,
                rotation: actor.rotation ?? 180,
                mobInventoryValue: actor.mobInventoryValue ?? null,
                mobInventoryItemsJson: actor.mobInventoryItemsJson ?? null,
            };
            bestDistanceSq = distanceSq;
        }

        return best;
    }

    private getContextTargetAtScreen(pointer: PixelPosition): HoveredActorInfo | null {
        const actor = this.getActorAtScreen(pointer);
        if (!actor || actor.kind === 'self') {
            return null;
        }
        return actor;
    }

    private isWithinActiveRadius(tile: TilePosition, center: TilePosition): boolean {
        const grid = this.mapLayer.getGrid();
        return grid.distance(Math.floor(center.x), Math.floor(center.y), tile.x, tile.y) <= this.getActiveRadiusFn();
    }
}
